import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { styled, useTheme } from '@mui/material/styles'; // Importa useTheme
import useMediaQuery from '@mui/material/useMediaQuery'; // Importa useMediaQuery

const drawerWidth = 200;

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

const TLALOC_EXTERNAL_URL = "https://www.tlaloc.rotoplas.com/";

function Sidebar({ isOpen, toggleSidebar, setAuth, role }) {
  const location = useLocation();
  const theme = useTheme(); // Accede al tema
  // Detecta si la pantalla es pequeña (menor que 'sm' breakpoint)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
{/*
  const navItems = [
    ...(role === 'admin' ? [
      { text: 'Home', path: '/home' },
      { text: 'Plantas', path: '/plantas' },
      { text: 'Usuarios', path: '/usuarios' },
      { text: 'Fabricantes', path: '/fabricantes' },
      { text: 'Tipos de Rutinas', path: '/tiporutinas' },
      { text: 'Rutinas', path: '/rutinas' },
      { text: 'Reportes', path: '/reportes' },
    ] : [
      { text: 'Home', path: '/home_usr' },
      { text: 'Rutinas', path: '/rutinas' },
      { text: 'Tlaloc', path: TLALOC_EXTERNAL_URL, external: true },
    ]),
  ];*/}



  let navItems = [];

if (role === 'admin'  ) {
  // --- Menú completo ---
  navItems = [
    { text: 'Home', path: '/home' },
    { text: 'Plantas', path: '/plantas' },
    { text: 'Usuarios', path: '/usuarios' },
    { text: 'Fabricantes', path: '/fabricantes' },
    { text: 'Tipos de Rutinas', path: '/tiporutinas' },
    { text: 'Rutinas', path: '/rutinas' },
    { text: 'Reportes', path: '/reportes' },
  ];
} else if (role === 'supervisor' || role === 'coordinador') {
  // --- Menú medio (home normal) ---
  navItems = [
    { text: 'Home', path: '/home' },
    { text: 'Rutinas', path: '/rutinas' },
    { text: 'Tlaloc', path: TLALOC_EXTERNAL_URL, external: true },
  ];
} else {
  // --- Menú básico (usuarios normales) ---
  navItems = [
    { text: 'Home', path: '/home_usr' },
    { text: 'Rutinas', path: '/rutinas' },
    { text: 'Tlaloc', path: TLALOC_EXTERNAL_URL, external: true },
  ];
}


  const getActiveClass = (path) => {
    return location.pathname === path ? 'active' : '';
  };

  return (
    <Drawer
      // Variante del Drawer: 'temporary' para móvil, 'persistent' para desktop
      variant={isMobile ? 'temporary' : 'persistent'}
      anchor="left"
      // 'open' ahora depende de isMobile. Si es móvil y no está abierto, se mantiene cerrado.
      // Si no es móvil, se comporta como antes (abierto por defecto o según el estado)
      open={isMobile ? isOpen : isOpen} // O simplemente 'open={isOpen}' si el control lo lleva el padre
      onClose={toggleSidebar} // Agrega onClose para que el Drawer temporal se cierre al tocar fuera
      sx={{
        width: isOpen ? drawerWidth :  0,
        flexShrink: 0,
        // Controla la visibilidad del papel del Drawer
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          backgroundColor: '#ffffff',
          boxShadow: '2px 0 5px rgba(0, 0, 0, 0.1)',
          borderRight: 'none',
          transition: theme.transitions.create('width', { // Usa la transición del tema para el ancho
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          // En móvil, la anchura del drawer es siempre el drawerWidth cuando está abierto.
          // En desktop, si isOpen es false, el ancho se reduce a 0.
          width: isMobile ? drawerWidth : (isOpen ? drawerWidth : 0),
          // Ajusta el margen para el contenido principal si el drawer es persistente
          // Si el drawer es persistente y está abierto, empuja el contenido
          // (Esto se haría típicamente en el contenedor principal de tu app)
        },
      }}
    >
      {/* El DrawerHeader con el botón de cerrar */}
      {isMobile || isOpen ? ( // Muestra el header si es móvil (siempre se cierra) o si está abierto en desktop
        <DrawerHeader>
          <IconButton onClick={toggleSidebar} sx={{ position: 'absolute', top: 10, right: 10 }}>
            <ChevronLeftIcon />
          </IconButton>
        </DrawerHeader>
      ) : null}


      <List>
        {navItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            {item.external ? (
              <ListItemButton
                component="a"
                href={item.path}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  '&:hover': {
                    backgroundColor: '#f0f0f0',
                  },
                  // La opacidad solo es relevante si el Drawer es 'persistent' y se esconde
                  opacity: isOpen || isMobile ? 1 : 0, // Si es móvil o está abierto, opacidad 1
                  transition: 'opacity 0.3s ease-in-out',
                  whiteSpace: 'nowrap',
                }}
              >
                <ListItemText primary={item.text} />
              </ListItemButton>
            ) : (
              <ListItemButton
                component={Link}
                to={item.path}
                className={getActiveClass(item.path)}
                sx={{
                  '&.active': {
                    backgroundColor: '#e0e0e0',
                    fontWeight: 'bold',
                  },
                  '&.active:hover': {
                    backgroundColor: '#d0d0d0',
                  },
                  '&:hover': {
                    backgroundColor: '#f0f0f0',
                  },
                  // La opacidad solo es relevante si el Drawer es 'persistent' y se esconde
                  opacity: isOpen || isMobile ? 1 : 0, // Si es móvil o está abierto, opacidad 1
                  transition: 'opacity 0.3s ease-in-out',
                  whiteSpace: 'nowrap',
                }}
              >
                <ListItemText primary={item.text} />
              </ListItemButton>
            )}
          </ListItem>
        ))}

        {/* Botón de cerrar sesión */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              localStorage.removeItem('token');
              setAuth(false);
            }}
            sx={{
              mt: 2,
              backgroundColor: '#424242',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#333333',
              },
              transition: 'background-color 0.3s ease-in-out',
              whiteSpace: 'nowrap',
            }}
          >
            <ListItemText primary="Cerrar sesión" />
          </ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  );
}

export default Sidebar;